import { OvertimeRequest, User } from '@/types'
import { OvertimeStatus, UserRole } from '@/types/enums'

/**
 * Filter overtime requests by user ID
 * @param overtimeRequests - Array of all overtime requests
 * @param userId - User identifier to filter by
 * @returns Filtered array of overtime requests for the specified user
 */
export const filterUserRequests = (
  overtimeRequests: OvertimeRequest[], 
  userId: string
): OvertimeRequest[] => {
  return overtimeRequests.filter(request => request.employeeId === userId)
}

/**
 * Filter overtime requests by department
 * @param overtimeRequests - Array of all overtime requests
 * @param department - Department identifier to filter by
 * @returns Filtered array of overtime requests for the specified department
 */
export const filterDepartmentRequests = (
  overtimeRequests: OvertimeRequest[], 
  department: string
): OvertimeRequest[] => {
  return overtimeRequests.filter(request => request.department === department);
}

/**
 * Safely checks if a request status matches one of the expected statuses
 * @param status - Status string to check
 * @param expectedStatuses - Array of expected status strings
 * @returns Boolean indicating if status matches any expected status
 */
export const hasStatusOf = (
  status: string | undefined | null,
  expectedStatuses: string[]
): boolean => {
  if (!status) return false;
  return expectedStatuses.some(s => status.toLowerCase() === s.toLowerCase());
};

/**
 * Filter pending overtime requests based on user role
 * @param overtimeRequests - Array of all overtime requests
 * @param user - Current user
 * @returns Filtered array of pending overtime requests relevant to the user
 */
export const filterPendingRequests = (
  overtimeRequests: OvertimeRequest[],
  user: User | null
): OvertimeRequest[] => {
  if (!user) return [];
  
  // For super_admin, show requests from plant managers or VP approval requests
  if (user.role === UserRole.SUPER_ADMIN) {
    return overtimeRequests.filter(req => 
      // 1. Pending requests created by plant_manager
      (req.status === OvertimeStatus.PENDING && req.created_by_role?.toLowerCase() === UserRole.PLANT_MANAGER) ||
      // 2. VP approval required requests (threshold exceeded) - from any role
      (req.status === OvertimeStatus.PENDING_VP_APPROVAL) ||
      // 3. Over-threshold department head requests requiring VP approval
      (req.status === OvertimeStatus.PENDING_VP_APPROVAL && req.created_by_role?.toLowerCase() === UserRole.DEPARTMENT_HEAD && req.vp_approval_required)
    );
  }
  
  // For plant_manager, show pending_pm_approval requests and legacy pending requests 
  if (user.role === UserRole.PLANT_MANAGER) {
    // Plant manager should see requests from ALL their departments
    if (!user.department || user.department.length === 0) return []; // No departments, no requests
    return overtimeRequests.filter(req => 
      user.department.includes(req.department as string) && ( // req.department is string
        // 1. Superintendent requests approved by DH, needing PM approval
        (req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT && 
         hasStatusOf(req.status, [OvertimeStatus.PENDING_PM_APPROVAL])) ||
        // 2. Legacy department head requests not requiring VP approval
        (req.created_by_role?.toLowerCase() === UserRole.DEPARTMENT_HEAD && 
         hasStatusOf(req.status, [OvertimeStatus.PENDING]) && 
         req.vp_approval_required === false)
      )
    );
  }
  
  // For department_head, show ONLY superintendent requests with pending_dh_approval status
  if (user.role === UserRole.DEPARTMENT_HEAD) {
    if (!user.department || user.department.length === 0) return []; // No departments, no requests
    return overtimeRequests.filter(req => 
      user.department.includes(req.department as string) && 
      req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT && 
      hasStatusOf(req.status, [OvertimeStatus.PENDING_DH_APPROVAL])
    );
  }
  
  // For superintendent, they don't approve requests - they only create them
  if (user.role === UserRole.SUPERINTENDENT) {
    return []; // Superintendents don't have pending requests to approve
  }
  
  // Default case (should not normally happen)
  return [];
}
